import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  organizationProfileFormSchema,
  type OrganizationProfileFormInput,
  type OrganizationProfileFormValues,
} from './organization-profile-schema'
import type { OrganizationProfileRow } from '@/types/organization-profile'

interface OrganizationProfileFormProps {
  profile: OrganizationProfileRow | null
  onSubmit: (data: OrganizationProfileFormValues) => void
  isLoading?: boolean
  isOwner?: boolean
}

// Common timezone options
const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Australia/Sydney',
]

// Common currency options
const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF']

// Common country options
const COUNTRY_OPTIONS = [
  'United States',
  'Canada',
  'United Kingdom',
  'France',
  'Germany',
  'Japan',
  'Australia',
  'New Zealand',
]

// Industry options
const INDUSTRY_OPTIONS = [
  'Charter',
  'Corporate',
  'Commercial',
  'Private',
  'Military',
  'Other',
]

export function OrganizationProfileForm({
  profile,
  onSubmit,
  isLoading,
  isOwner = false,
}: OrganizationProfileFormProps) {
  const isReadOnly = !isOwner

  const form = useForm<OrganizationProfileFormInput, unknown, OrganizationProfileFormValues>({
    resolver: zodResolver(organizationProfileFormSchema),
    defaultValues: {
      legal_name: profile?.legal_name || '',
      tax_id: profile?.tax_id || '',
      industry: profile?.industry || '',
      timezone: profile?.timezone || 'America/New_York',
      currency: profile?.currency || 'USD',
      logo_url: profile?.logo_url || '',
      primary_color: profile?.primary_color || '#000000',
      primary_phone: profile?.primary_phone || '',
      primary_email: profile?.primary_email || '',
      website_url: profile?.website_url || '',
      address_line1: profile?.address_line1 || '',
      city: profile?.city || '',
      state_province: profile?.state_province || '',
      postal_code: profile?.postal_code || '',
      country: profile?.country || '',
    },
  })

  const isDirty = form.formState.isDirty

  const handleSubmit = form.handleSubmit((data) => {
    // Convert empty strings to undefined for the API
    const cleanData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, value === '' ? undefined : value])
    ) as OrganizationProfileFormValues
    onSubmit(cleanData)
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization Profile</CardTitle>
        <CardDescription>
          {isReadOnly
            ? 'View your organization details. Only owners can edit this information.'
            : 'Manage your organization details and settings.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-6">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="branding">Branding</TabsTrigger>
                <TabsTrigger value="contact">Contact</TabsTrigger>
                <TabsTrigger value="address">Address</TabsTrigger>
              </TabsList>

              {/* General Tab */}
              <TabsContent value="general" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="legal_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Legal Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Your organization's legal name"
                          {...field}
                          disabled={isReadOnly || isLoading}
                        />
                      </FormControl>
                      <FormDescription>
                        The official legal name of your organization
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tax_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tax ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., EIN or VAT ID"
                          {...field}
                          disabled={isReadOnly || isLoading}
                        />
                      </FormControl>
                      <FormDescription>
                        Tax identification number (EIN, VAT ID, etc.)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Industry</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value || ''}
                        disabled={isReadOnly || isLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select an industry" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {INDUSTRY_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="timezone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Timezone</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value || 'America/New_York'}
                        disabled={isReadOnly || isLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select timezone" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIMEZONE_OPTIONS.map((tz) => (
                            <SelectItem key={tz} value={tz}>
                              {tz}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value || 'USD'}
                        disabled={isReadOnly || isLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CURRENCY_OPTIONS.map((curr) => (
                            <SelectItem key={curr} value={curr}>
                              {curr}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              {/* Branding Tab */}
              <TabsContent value="branding" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="logo_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Logo URL</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://example.com/logo.png"
                          type="url"
                          {...field}
                          disabled={isReadOnly || isLoading}
                        />
                      </FormControl>
                      <FormDescription>
                        URL to your organization's logo image
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="primary_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Color</FormLabel>
                      <div className="flex gap-2 items-center">
                        <FormControl>
                          <Input
                            type="color"
                            {...field}
                            disabled={isReadOnly || isLoading}
                            className="h-10 w-20"
                          />
                        </FormControl>
                        <span className="text-sm text-gray-500">{field.value}</span>
                      </div>
                      <FormDescription>
                        Your organization's primary brand color (hex format)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              {/* Contact Tab */}
              <TabsContent value="contact" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="primary_phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="+1 (555) 123-4567"
                          {...field}
                          disabled={isReadOnly || isLoading}
                        />
                      </FormControl>
                      <FormDescription>
                        Primary contact phone number
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="primary_email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="info@example.com"
                          type="email"
                          {...field}
                          disabled={isReadOnly || isLoading}
                        />
                      </FormControl>
                      <FormDescription>
                        Primary contact email address
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="website_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website URL</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://example.com"
                          type="url"
                          {...field}
                          disabled={isReadOnly || isLoading}
                        />
                      </FormControl>
                      <FormDescription>
                        Your organization's website
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              {/* Address Tab */}
              <TabsContent value="address" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="address_line1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="123 Main Street"
                          {...field}
                          disabled={isReadOnly || isLoading}
                        />
                      </FormControl>
                      <FormDescription>
                        Street address
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="New York"
                          {...field}
                          disabled={isReadOnly || isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="state_province"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State / Province</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="New York"
                          {...field}
                          disabled={isReadOnly || isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="postal_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Postal Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="10001"
                          {...field}
                          disabled={isReadOnly || isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value || ''}
                        disabled={isReadOnly || isLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COUNTRY_OPTIONS.map((country) => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>
            </Tabs>

            {/* Submit Button */}
            {!isReadOnly && (
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => form.reset()}
                  disabled={!isDirty || isLoading}
                >
                  Reset
                </Button>
                <Button type="submit" disabled={!isDirty || isLoading}>
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            )}

            {isReadOnly && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                Only organization owners can edit this information.
              </div>
            )}
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
